import netlifyIdentity from 'netlify-identity-widget';
import { useState, useEffect, useCallback } from 'react';
import type { User } from '../types';
import type { User as NetlifyUser } from 'netlify-identity-widget';

/**
 * Manages authentication state using Netlify Identity.
 * Wraps the netlify-identity-widget for React integration.
 */
/**
 * Normalize Netlify Identity user object to match app's expected format
 */
function normalizeUser(identityUser: NetlifyUser | null): User | null {
  if (!identityUser) return null;

  return {
    userId: identityUser.id,
    email: identityUser.email,
    name: identityUser.user_metadata?.full_name || identityUser.email,
    picture: identityUser.user_metadata?.avatar_url || undefined,
    // Keep original for reference
    _raw: identityUser,
  };
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // SECURITY: Auth bypass is only available in development builds
    // The entire useAuth.dev.ts module is tree-shaken out of production
    if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
      // Dynamic import ensures this code chunk is only loaded in dev mode
      import('./useAuth.dev').then(({ getDevUser }) => {
        setUser(getDevUser());
        setLoading(false);
      });
      return;
    }

    // Check if we're in the middle of an OAuth callback
    // Netlify Identity uses URL hash for OAuth redirects
    const isOAuthCallback = window.location.hash.includes('confirmation_token') ||
                           window.location.hash.includes('recovery_token') ||
                           window.location.hash.includes('invite_token') ||
                           window.location.hash.includes('access_token');

    // Listen for init event (fired when widget is ready)
    const handleInit = (identityUser: NetlifyUser | null) => {
      setUser(normalizeUser(identityUser));
      // Only stop loading if we're not processing an OAuth callback
      // If we are in OAuth callback, wait for 'login' event
      if (!isOAuthCallback) {
        setLoading(false);
      }
    };

    // Listen for login events (fires after OAuth completes or manual login)
    const handleLogin = (identityUser: NetlifyUser) => {
      setUser(normalizeUser(identityUser));
      setLoading(false); // OAuth callback complete, stop loading
      netlifyIdentity.close();
    };

    // Listen for logout events
    const handleLogout = () => {
      setUser(null);
    };

    // Register event listeners BEFORE init
    netlifyIdentity.on('init', handleInit);
    netlifyIdentity.on('login', handleLogin);
    netlifyIdentity.on('logout', handleLogout);

    // Initialize Netlify Identity (triggers 'init' event when ready)
    netlifyIdentity.init();

    // Cleanup listeners on unmount
    return () => {
      netlifyIdentity.off('login', handleLogin);
      netlifyIdentity.off('logout', handleLogout);
      netlifyIdentity.off('init', handleInit);
    };
  }, []);

  const login = useCallback((): void => {
    netlifyIdentity.open('login');
  }, []);

  const logout = useCallback((): void => {
    netlifyIdentity.logout();
  }, []);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    const identityUser = netlifyIdentity.currentUser();
    const normalized = normalizeUser(identityUser);
    setUser(normalized);
    return normalized;
  }, []);

  return {
    user,
    loading,
    error: null, // Netlify Identity handles errors via UI modal
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };
}
